/**
 * Admin Licence Detail; the Overview's pure rules (#565): who is active, what the
 * "At a glance" rows say, whether onboarding is done, and how the three KPI dates
 * read. No React, no Redux, no fetch; every rule is a plain function tested as one.
 */

import { SUBSCRIPTION_STATUS } from '@shared/constants/subscriptionStatus';
import type { LicenceResponse, ListResponse, ProfilesDelivered, UserResponse } from '@shared/schemas';

import { daysSince, relativeDaysLabel } from '@/shared/time/recency';
import { formatDateFr } from '@/utils/format';

import { profilesDeliveredLabel } from '../../_shared/domain/profiles-delivered';

// ── Users, lists, onboarding ──

/** An archived user no longer belongs to the licence's audience, whatever their status says. */
const countWithStatus = (users: readonly UserResponse[], status: string) =>
  users.filter((u) => u.subscription_status === status && !u.archived_at).length;

/** Users who activated their account (archived ones excluded). */
export function countActiveUsers(users: readonly UserResponse[]): number {
  return countWithStatus(users, SUBSCRIPTION_STATUS.ACTIVE);
}

/** Users invited and not yet active (archived ones excluded). */
export function countPendingUsers(users: readonly UserResponse[]): number {
  return countWithStatus(users, SUBSCRIPTION_STATUS.PENDING);
}

export type OnboardingState = 'completed' | 'in_progress';

/**
 * Onboarding is computed, not recorded: a licence is onboarded once at least one of
 * its users is active AND it has at least one list. Anything short of both is in
 * progress; there is no backend field to disagree with this.
 */
export function onboardingState(activeUsers: number, listCount: number): OnboardingState {
  return activeUsers > 0 && listCount > 0 ? 'completed' : 'in_progress';
}

export const ONBOARDING_LABELS: Record<OnboardingState, string> = {
  completed: 'Completed',
  in_progress: 'In progress',
};

/** The "At a glance" figures of the identity panel. */
export interface LicenceGlance {
  activeUsers: number;
  pendingUsers: number;
  listCount: number;
  onboarding: OnboardingState;
  /** The running contract period's figures (#750); null without one. */
  profilesDelivered: ProfilesDelivered | null;
}

export function licenceGlance(
  users: readonly UserResponse[],
  lists: readonly ListResponse[],
  licence: Pick<LicenceResponse, 'profiles_delivered'> | null = null,
): LicenceGlance {
  const activeUsers = countActiveUsers(users);
  return {
    activeUsers,
    pendingUsers: countPendingUsers(users),
    listCount: lists.length,
    onboarding: onboardingState(activeUsers, lists.length),
    profilesDelivered: licence?.profiles_delivered ?? null,
  };
}

/** `6 active` · `6 active · 2 pending`: the pending half only when there is one. */
export function glanceUsersLabel(glance: Pick<LicenceGlance, 'activeUsers' | 'pendingUsers'>): string {
  const active = `${glance.activeUsers} active`;
  return glance.pendingUsers > 0 ? `${active} · ${glance.pendingUsers} pending` : active;
}

/** `1 list` · `19 lists`: a count of lists, never of the profiles inside them. */
export function glanceListsLabel(listCount: number): string {
  return `${listCount} ${listCount === 1 ? 'list' : 'lists'}`;
}

/** The folded rail's tooltip: the rows in one line. */
export function glanceSummary(glance: LicenceGlance): string {
  return [
    `Users: ${glanceUsersLabel(glance)}`,
    `Lists: ${glanceListsLabel(glance.listCount)}`,
    `Profiles Delivered: ${profilesDeliveredLabel(glance.profilesDelivered)}`,
    `Onboarding: ${ONBOARDING_LABELS[glance.onboarding]}`,
  ].join(' · ');
}

// ── KPI dates ──

/** `10:24`: the 24-hour clock, like `formatDateTimeFr`. */
export function formatClockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/** One KPI card's text. `empty` greys the value: nothing is recorded. */
export interface OverviewKpi {
  value: string;
  /** How long ago, in words; absent when the value already says it (today, yesterday) or when empty. */
  caption: string | null;
  empty: boolean;
}

/**
 * A dated KPI. Days are the calendar days of `shared/time/recency`, the rule every
 * relative date follows: `Today, 23:10` can only ever be a time that has happened today.
 */
function datedKpi(iso: string, now: Date): OverviewKpi {
  const days = daysSince(iso, now);
  if (days === 0) return { value: `Today, ${formatClockTime(iso)}`, caption: null, empty: false };
  if (days === 1) return { value: 'Yesterday', caption: null, empty: false };
  return { value: formatDateFr(iso), caption: relativeDaysLabel(days), empty: false };
}

const emptyKpi = (value: string): OverviewKpi => ({ value, caption: null, empty: true });

/** Last list sent: `-` when no list has a recorded send (same as the Licences index). */
export function lastListSentKpi(
  licence: Pick<LicenceResponse, 'last_list_sent_at'>,
  now: Date = new Date(),
): OverviewKpi {
  return licence.last_list_sent_at ? datedKpi(licence.last_list_sent_at, now) : emptyKpi('-');
}

/** Last client login: `Never connected` when none of its client users signed in. */
export function lastClientLoginKpi(
  licence: Pick<LicenceResponse, 'last_client_login_at'>,
  now: Date = new Date(),
): OverviewKpi {
  return licence.last_client_login_at
    ? datedKpi(licence.last_client_login_at, now)
    : emptyKpi('Never connected');
}

/**
 * Last admin activity. Undated, it means two things (as on the Licences index): a
 * licence created after tracking shipped was simply never worked on: `Never used`;
 * one created before has no history to show, and "never" would be false: `-`.
 */
export function lastAdminActivityKpi(
  licence: Pick<LicenceResponse, 'last_admin_activity_at' | 'created_before_activity_tracking'>,
  now: Date = new Date(),
): OverviewKpi {
  if (licence.last_admin_activity_at) return datedKpi(licence.last_admin_activity_at, now);
  return emptyKpi(licence.created_before_activity_tracking ? '-' : 'Never used');
}
